<template>
  <AppModal
    :show="show"
    :title="title"
    :size="'medium'"
    :gradientHeader="false"
    :loading="isLoading"
    :closeOnBackdropClick="true"
    @close="cancel"
  >
    <!-- Icon and message content -->
    <div class="confirmation-dialog__content">
      <div class="confirmation-dialog__message-section">
        <div v-if="iconType" class="confirmation-dialog__icon" :class="`confirmation-dialog__icon--${iconType}`">
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path v-if="iconType === 'info'" fill="currentColor" d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
            <path v-else-if="iconType === 'warning'" fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
            <path v-else-if="iconType === 'danger'" fill="currentColor" d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"/>
            <path v-else-if="iconType === 'success'" fill="currentColor" d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
          </svg>
        </div>
        <div class="confirmation-dialog__text">
          <p v-if="message" class="confirmation-dialog__message">{{ message }}</p>
          <slot></slot>
        </div>
      </div>
    </div>    <!-- Custom footer -->
    <template #footer="{ close }">
      <div class="confirmation-dialog__actions">
        <AppButton 
          type="button" 
          variant="outline"
          @click="() => cancel(close)"
          ref="cancelBtn"
          :disabled="isLoading"
          class="confirmation-dialog__btn confirmation-dialog__btn--cancel"
        >
          {{ cancelButtonText }}
        </AppButton>
        <AppButton 
          type="button"
          :variant="confirmButtonVariant"
          @click="() => confirm(close)"
          :disabled="isLoading"
          :loading="isLoading"
          class="confirmation-dialog__btn confirmation-dialog__btn--confirm"
        >
          {{ confirmButtonText }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, watch, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';

const props = defineProps<{
  show?: boolean;
  title?: string;
  message?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonType?: 'primary' | 'danger' | 'warning' | 'success';
  icon?: string;
  iconType?: 'info' | 'warning' | 'danger' | 'success';
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'confirm'): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();
const cancelBtn = ref<any>(null);

const confirm = (close?: () => void) => {
  emit('confirm');
  if (close) close();
};

const cancel = (close?: () => void) => {
  emit('cancel');
  if (close) close();
};

// Compute button variant based on type
const confirmButtonVariant = computed(() => {
  const variants: Record<string, string> = {
    primary: 'primary',
    danger: 'danger',
    warning: 'warning',
    success: 'success'
  };
  return variants[props.confirmButtonType || 'primary'] || 'primary';
});

// Watch for dialog visibility and focus management
watch(() => props.show, (newVal: boolean | undefined) => {
  if (newVal) {
    nextTick(() => {
      if (cancelBtn.value && cancelBtn.value.$el) {
        cancelBtn.value.$el.focus();
      }
    });
  }
});
</script>

<style scoped>
/* ==========================================================================
   CONFIRMATION DIALOG STYLES
   ========================================================================== */

.confirmation-dialog__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.confirmation-dialog__message-section {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
}

.confirmation-dialog__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-full);
}

.confirmation-dialog__icon--info {
  background: var(--primary-100);
  color: var(--primary);
}

.confirmation-dialog__icon--warning {
  background: var(--warning-100);
  color: var(--warning);
}

.confirmation-dialog__icon--danger {
  background: var(--danger-100);
  color: var(--danger);
}

.confirmation-dialog__icon--success {
  background: var(--success-100);
  color: var(--success);
}

.confirmation-dialog__text {
  flex: 1;
  min-width: 0;
}

.confirmation-dialog__message {
  font-size: var(--font-size-md);
  color: var(--text-color-secondary);
  margin: 0;
  line-height: 1.5;
}

.confirmation-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
}

.confirmation-dialog__btn {
  min-width: 100px;
}

/* Responsive design */
@media (max-width: 640px) {
  .confirmation-dialog__message-section {
    gap: var(--space-3);
  }
  
  .confirmation-dialog__icon {
    width: 48px;
    height: 48px;
  }
  
  .confirmation-dialog__message {
    font-size: var(--text-sm);
  }
  
  .confirmation-dialog__actions {
    flex-direction: column-reverse;
    gap: var(--space-2);
  }
  
  .confirmation-dialog__btn {
    width: 100%;
  }
}
</style>
